import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import ContentCard from "./ContentCard";
import SSOApiService from "../../../services/SsoServices";
import { SSO_CONTENT_LIST } from "../../../constants/api-constants";

/*istanbul ignore next */
const ContentList = ({ searchText, filters }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);
  const pageNumberRef = useRef(1);
  const isLoadingRef = useRef(false);

  const loadContent = useCallback(async (page, isInitial = false) => {
    if (isLoadingRef.current && !isInitial) return;
    try {
      isLoadingRef.current = true;

      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const filterPayload = {
        contentType: filters?.contentType || [],
        typeOfContent: filters?.typeOfContent || [],
        typeOfForm:
          filters?.typeOfForm?.map((id) => ({ typeOfFormId: id })) || [],
        lineOfBusiness:
          filters?.lineOfBusiness?.map((id) => ({ lineOfBusinessId: id })) ||
          [],
        countries: filters?.countries?.map((id) => ({ countryId: id })) || [],
        states: filters?.states?.map((id) => ({ stateId: id })) || [],
        contentFillable: filters?.contentFillable || [],
        userIds: filters?.userIds ?? (filters?.distributors || []),
        selectedIds: filters?.selectedIds || [],
        selectedColumns: filters?.selectedColumns || [],
        selectedContentOriginators: filters?.contentOriginators || [],
        contentOriginator: "",
        subscriberId: 0,
      };
      const response = await SSOApiService.ssoPost(SSO_CONTENT_LIST, {
        isShowChildUserData: true,
        searchText: searchText,
        selectedIds: [],
        pageSize: 10,
        pageNumber: page,
        ignorePaging: false,
        sortColumn: "",
        sortType: "",
        filter: filterPayload,
      });

      const newRecords = response?.result?.records || [];
      const totalRecords = response?.result?.totalRecords || 0;

      if (isInitial) {
        setData(newRecords);
        setHasMore(newRecords.length < totalRecords);
      } else {
        setData((prevData) => {
          const updatedData = [...prevData, ...newRecords];
          setHasMore(updatedData.length < totalRecords);
          return updatedData;
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load content", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [filters, searchText]);

  useEffect(() => {
    pageNumberRef.current = 1;
    setData([]);
    setHasMore(true);
    loadContent(1, true);
  }, [searchText, filters, loadContent]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingRef.current &&
          !loading &&
          data.length > 0
        ) {
          pageNumberRef.current += 1;
          loadContent(pageNumberRef.current);
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, data.length, loadContent]);

  const normalize = (content) => {
    const subscriptionTypeMap = {
      1: "Monthly",
      2: "Quarterly",
      3: "Semi-Annual",
      4: "Annual",
      99: "One Time",
    };

    // Priority order for displaying subscription types
    const priorityOrder = [99, 1, 2, 3, 4];

    const priceItems = [];

    // Add one-time cost if available
    if (content.oneTimeCost && content.oneTimeCost > 0) {
      priceItems.push({
        type: 99,
        value: content.oneTimeCost,
        label: "One Time",
        isRecurring: false,
      });
    }

    // Collect prices from subscriptions array
    if (content.subscriptions && content.subscriptions.length > 0) {
      content.subscriptions.forEach((sub) => {
        if (sub.status === 1) {
          const value = sub.actualValue || sub.baseValue || 0;
          const label = subscriptionTypeMap[sub.frequency] || "Unknown";
          priceItems.push({
            type: sub.frequency,
            value: value,
            label: label,
            isRecurring: sub.isRecurring || false,
            contentSubscriptionId: sub.contentSubscriptionId,
          });
        }
      });
    }

    // Check if content is free (no prices collected)
    const isFree = priceItems.length === 0;

    // Sort by priority order
    priceItems.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.type);
      const indexB = priorityOrder.indexOf(b.type);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    // Format price for display (only first item)
    const formatPrice = () => {
      if (isFree) return "$0";
      const firstItem = priceItems[0];
      return `$${firstItem.value} – ${firstItem.label}`;
    };

    const firstSub = content.subscriptions?.[0];
    return {
      id: content.contentId,
      formNumber: content.formNumber,
      latestVersion: content.latestVersion,
      name: content.name,
      type: content.typeOfContent,
      typeOfContent: content.typeOfContent,
      brand: content.brandName,
      distributor: content.userName,
      version: content.latestVersion,
      isPreview: content.isPreview,
      previewType: content.previewType,
      marketingDescription: content.marketingDescription,
      isLink: !!content.webLink,
      url: content.webLink || content.uniqueFileName,
      price: formatPrice(),
      subscriptionOptions: priceItems, // All available subscriptions
      defaultSubscription: priceItems[0] || null, // First one (highest priority)
      recurring: firstSub?.isRecurring,
      isPurchased: firstSub?.isAssociated,
      isAddedInCart: content.isAddedInCart || false,
      documentType: content.documentType,
      totalContent: content.totalContent || 0,
      contents: content.contents || [],
    };
  };

  const normalized = data.map(normalize);

  if (loading)
    return (
      <div
        className="sso-loader-wrapper"
        style={{ padding: "20px", color: "white" }}
      >
        <div className="sso-loader" />
        Loading Available Content
      </div>
    );

  return (
    <div className="content-list">
      {normalized.length === 0 && (
        <div style={{ padding: "20px", textAlign: "center", color: "white" }}>
          No Available Content Found
        </div>
      )}
      {normalized.map((item) => (
        <ContentCard key={item.id} item={item} />
      ))}
      {hasMore && <div ref={observerTarget} style={{ height: "20px" }} />}
      {loadingMore && (
        <div
          className="sso-loader-wrapper"
          style={{ padding: "20px", color: "white" }}
        >
          <div className="sso-loader" />
          Loading More Content...
        </div>
      )}
    </div>
  );
};
ContentList.propTypes = {
  searchText: PropTypes.string.isRequired,
  filters: PropTypes.object,
};

ContentList.defaultProps = {
  filters: {},
};

export default ContentList;
